import { View, Text, Pressable, StyleSheet } from 'react-native'
import { useRouter, usePathname } from 'expo-router'
import { Shield, LayoutDashboard, Mail, FileText, MessageSquare, Settings, LogOut } from 'lucide-react-native'
import { useCallback } from 'react'
import Animated, { useAnimatedStyle, withTiming, runOnJS } from 'react-native-reanimated'
import { useAuth } from '../auth/AuthProvider'
import { storage, TOKEN_KEY, USER_ID_KEY } from '../utils/storage'
import { colors, spacing, borderRadius } from '../theme'
import { MOBILE_HEADER_HEIGHT } from './MobileHeader'

const NAV_ITEMS = [
  { segment: 'index', label: 'Oversigt', Icon: LayoutDashboard },
  { segment: 'inbox', label: 'Indbakke', Icon: Mail },
  { segment: 'quotes', label: 'Tilbud', Icon: FileText },
  { segment: 'chat', label: 'Chat', Icon: MessageSquare },
  { segment: 'settings', label: 'Indstillinger', Icon: Settings },
]

interface MobileSidebarProps {
  visible: boolean
  onClose: () => void
  unreadCount: number
}

export default function MobileSidebar({ visible, onClose, unreadCount }: MobileSidebarProps) {
  const { user, logout } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const translateX = useAnimatedStyle(() => ({
    transform: [{ translateX: withTiming(visible ? 0 : -288, { duration: 300 }) }],
  }))

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: withTiming(visible ? 1 : 0, { duration: 300 }),
    pointerEvents: visible ? 'auto' as const : 'none' as const,
  }))

  const handleLogout = useCallback(async () => {
    await storage.remove(TOKEN_KEY)
    await storage.remove(USER_ID_KEY)
    logout()
    onClose()
  }, [logout, onClose])

  const isActive = (segment: string) => {
    if (segment === 'index') return pathname === '/' || pathname === '/index'
    return pathname === `/${segment}` || pathname?.startsWith(`/${segment}/`)
  }

  const handleNav = (segment: string) => {
    const path = segment === 'index' ? '/(tabs)/' : `/(tabs)/${segment}`
    router.navigate(path as any)
    onClose()
  }

  return (
    <>
      {/* Overlay */}
      <Animated.View style={[styles.overlay, overlayStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      </Animated.View>

      {/* Sidebar panel */}
      <Animated.View style={[styles.sidebar, translateX]}>
        {/* Logo — links to dashboard (home) */}
        <Pressable onPress={() => { router.navigate('/(tabs)/' as any); onClose() }} style={styles.logoRow}>
          <Shield size={28} color={colors.primary} />
          <Text style={styles.logoText}>ForbrugerAgenten</Text>
        </Pressable>

        {/* Agent Email Card removed — users don't need to see/use the agent email */}

        {/* Navigation */}
        <View style={styles.nav}>
          {NAV_ITEMS.map(({ segment, label, Icon }) => {
            const active = isActive(segment)
            return (
              <Pressable
                key={segment}
                onPress={() => handleNav(segment)}
                style={[styles.navItem, active && styles.navItemActive]}
              >
                <Icon size={20} color={active ? '#fff' : colors.text.secondary} />
                <Text style={[styles.navLabel, active && styles.navLabelActive]}>{label}</Text>
                {segment === 'inbox' && unreadCount > 0 && (
                  <View style={[styles.badge, active && styles.badgeActive]}>
                    <Text style={styles.badgeText}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Text>
                  </View>
                )}
              </Pressable>
            )
          })}
        </View>

        {/* User info + logout */}
        <View style={styles.userSection}>
          <View style={styles.userInfo}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {user?.full_name?.[0] || user?.email?.[0] || '?'}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.userName} numberOfLines={1}>
                {user?.full_name || 'Bruger'}
              </Text>
              <Text style={styles.userEmail} numberOfLines={1}>{user?.email}</Text>
            </View>
          </View>
          <Pressable onPress={handleLogout} style={styles.logoutBtn}>
            <LogOut size={16} color={colors.error} />
            <Text style={styles.logoutText}>Log ud</Text>
          </Pressable>
        </View>
      </Animated.View>
    </>
  )
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    top: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 55,
  },
  sidebar: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 288,
    backgroundColor: colors.background,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    zIndex: 56,
  },
  logoRow: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  logoText: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text.primary,
  },
  nav: {
    padding: 16,
    marginTop: 8,
    gap: 4,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: borderRadius.md,
  },
  navItemActive: {
    backgroundColor: colors.primary,
  },
  navLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
    color: colors.text.secondary,
  },
  navLabelActive: {
    color: '#fff',
  },
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  badgeActive: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#fff',
  },
  userSection: {
    position: 'absolute',
    bottom: 84, // Clear the tab bar
    left: 0,
    right: 0,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    backgroundColor: colors.surface,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
  },
  userEmail: {
    fontSize: 12,
    color: '#6b7280',
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  logoutText: {
    fontSize: 14,
    color: colors.error,
  },
})
